/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent } from 'preact';
import {
  HTMLAttributes,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import '@adobe-commerce/elsie/components/TextSwatch/TextSwatch.css';
import { useText } from '@adobe-commerce/elsie/i18n';

export interface TextSwatchProps
  extends Omit<HTMLAttributes<HTMLInputElement>, 'label'> {
  name?: string;
  label: string;
  groupAriaLabel?: string;
  value?: string;
  id: string;
  disabled?: boolean;
  selected?: boolean;
  outOfStock?: boolean;
  multi?: boolean;
  onValue?: (value: any) => void;
  onUpdateError?: (error: Error) => void;
}

export const TextSwatch: FunctionComponent<TextSwatchProps> = ({
  className,
  name,
  value,
  label,
  groupAriaLabel,
  id,
  disabled = false,
  selected = false,
  outOfStock = false,
  multi = false,
  onValue,
  onUpdateError,
  ...props
}) => {
  // i18n
  const outOfStockLabel = useText('Dropin.Swatches.outOfStock.label').label;
  const selectedLabel = useText('Dropin.Swatches.selected.label').label;
  const swatchLabel = useText('Dropin.Swatches.swatch.label').label;

  const [isOverflowing, setIsOverflowing] = useState(false);
  const spanRef = useRef<HTMLLabelElement>(null);

  const onValueHandler = useCallback(
    async (newValue: any) => {
      if (onValue) {
        try {
          await onValue(newValue);
        } catch (e) {
          if (onUpdateError) {
            onUpdateError(e as Error);
          }
        }
      }
    },
    [onValue, onUpdateError]
  );

  const handleOnValue = (e: Event) => {
    const input = e.target as HTMLInputElement;

    onValueHandler(input.value);
  };

  const handleAriaLabel = () => {
    if (outOfStock) return `${groupAriaLabel}: ${label} ${outOfStockLabel}`;
    if (selected) return `${groupAriaLabel}: ${label} ${selectedLabel}`;
    return `${groupAriaLabel}: ${label} ${swatchLabel}`;
  };

  useEffect(() => {
    if (
      spanRef.current &&
      spanRef.current.scrollWidth > spanRef.current.clientWidth
    ) {
      setIsOverflowing(true);
    }
  }, [label]);

  const uniqueId = useMemo(() => id ?? `${name}_${id}_${Math.random().toString(36)}`, [name, id]);

  return (
    <div
      className="dropin-text-swatch__container"
      {...(isOverflowing ? { 'data-tooltip': label } : {})}
    >
      <input
        type={multi ? 'checkbox' : 'radio'}
        name={name}
        id={uniqueId}
        value={value}
        aria-label={handleAriaLabel()}
        checked={selected}
        disabled={disabled}
        onChange={handleOnValue}
        {...props}
        className={classes([
          'dropin-text-swatch',
          ['dropin-text-swatch--selected', selected],
          ['dropin-text-swatch--disabled', disabled],
          className,
        ])}
      />
      <label
        htmlFor={uniqueId}
        ref={spanRef}
        className={classes([
          'dropin-text-swatch__label',
          ['dropin-text-swatch__label--out-of-stock', outOfStock],
          className,
        ])}
      >
        {label}
      </label>
    </div>
  );
};
